<template>
  <div class="lyr-panel">
    <!-- Header -->
    <div class="lyr-header">
      <h2>六爻卜卦紀錄</h2>
      <p class="hint">
        共 {{ total }} 筆。點「檢視」可展開完整卦盤、填占卦者名字、標記已驗證、寫心得。
      </p>
    </div>

    <div v-if="error" class="error-banner">⚠ {{ error }}</div>

    <!-- 工具列 -->
    <div class="toolbar">
      <input
        v-model="searchQ"
        class="search-input"
        placeholder="搜 user_id / 題目 / 干支 / 占卦者"
        @keyup.enter="onSearch"
      />
      <button class="btn" @click="onSearch">搜</button>
      <label class="chk-inline">
        <input type="checkbox" v-model="verifiedOnly" @change="onSearch" />
        只看已驗證
      </label>
      <button class="btn secondary" @click="fetchList" :disabled="loading">
        {{ loading ? "讀取中…" : "重新載入" }}
      </button>
    </div>

    <!-- 列表 -->
    <div v-if="loading" class="loading"><em>載入中…</em></div>
    <div v-else-if="items.length === 0" class="empty"><em>沒有資料</em></div>
    <table v-else class="lyr-table">
      <thead>
        <tr>
          <th class="col-id">ID</th>
          <th class="col-time">時間</th>
          <th class="col-querent">占卦者</th>
          <th class="col-gender">性別</th>
          <th class="col-topic">題目</th>
          <th class="col-hex">起卦碼</th>
          <th class="col-action"></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in items" :key="row.id" :class="{ verified: row.is_verified }">
          <td class="mono">{{ row.id }}</td>
          <td class="col-time">{{ formatGongli(row.gongli) || "-" }}</td>
          <td>
            <span v-if="row.querent_name">{{ row.querent_name }}</span>
            <span v-else class="muted">(未填)</span>
          </td>
          <td>{{ row.gender_text || "-" }}</td>
          <td class="topic-cell" :title="row.topic_text">
            {{ row.topic_text || "-" }}
          </td>
          <td class="mono small">{{ row.hex_code || "-" }}</td>
          <td class="action-cell">
            <button class="btn small" @click="openDetail(row.id)">檢視</button>
            <span v-if="row.is_verified" class="badge verified" title="已驗證">✓</span>
            <span v-if="row.has_notes" class="badge notes" title="已寫心得">📝</span>
            <span v-if="row.has_shensha" class="badge shensha" title="已補卦身/用神/神煞">🔮</span>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- 分頁 -->
    <div class="pager">
      第 {{ page }} / {{ totalPages }} 頁
      <button class="btn small" :disabled="page <= 1 || loading" @click="goto(page - 1)">
        ← 上一頁
      </button>
      <button class="btn small" :disabled="page >= totalPages || loading" @click="goto(page + 1)">
        下一頁 →
      </button>
    </div>

    <!-- Modal -->
    <div v-if="detail" class="modal-mask" @click.self="closeDetail">
      <div class="modal">
        <div class="modal-head">
          <h3>六爻紀錄 #{{ detail.id }}</h3>
          <button class="close-btn" @click="closeDetail">×</button>
        </div>

        <div class="modal-body">
          <!-- 基本資訊 -->
          <div class="meta">
            <div class="meta-row"><label>時間</label><span>{{ formatFullDt(detail.created_at) }}</span></div>
            <div class="meta-row"><label>User</label><span class="mono small">{{ detail.user_id }}</span></div>
            <div class="meta-row"><label>性別</label><span>{{ detail.gender_text || "-" }}</span></div>
            <div class="meta-row"><label>起卦碼</label><span class="mono">{{ detail.hex_code || "-" }}</span></div>
            <div class="meta-row"><label>題目</label><span>{{ detail.topic_text || "-" }}</span></div>
          </div>

          <!-- ═══ 六爻卦盤區 ═══ -->
          <section class="hex-section">
            <!-- 頂部：日期 + 四柱 + 空亡 -->
            <div class="hex-topbar">
              <div class="hex-datebox">
                <div class="date-gongli">{{ hexInfo.gongli || "(無日期)" }}</div>
                <div class="date-nongli">{{ hexInfo.nongli || "" }}</div>
              </div>
              <table class="ganzhi-table" v-if="hexInfo.ganzhi.length === 4">
                <thead>
                  <tr>
                    <th>時</th><th>日</th><th>月</th><th>年</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td v-for="(gz, i) in ganzhiRev" :key="i">
                      <div><span :class="'wx-' + wuxingOf(gz[0])">{{ gz[0] }}</span></div>
                      <div><span :class="'wx-' + wuxingOf(gz[1])">{{ gz[1] }}</span></div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="xunkong-bar" v-if="hexInfo.xunkong.length === 4">
              <strong>空亡</strong>
              年【{{ hexInfo.xunkong[0] }}】
              月【{{ hexInfo.xunkong[1] }}】
              日【{{ hexInfo.xunkong[2] }}】
              時【{{ hexInfo.xunkong[3] }}】
            </div>

            <!-- 主卦盤：8 欄 -->
            <div v-if="!showRawFallback" class="hex-chart-wrap">
              <table class="hex-chart">
                <thead>
                  <tr>
                    <th>伏神</th>
                    <th>六親</th>
                    <th>六獸</th>
                    <th>六親</th>
                    <th>地支</th>
                    <th>本卦</th>
                    <th>變卦</th>
                    <th>六親</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(line, i) in hexLines" :key="i" :class="{ 'is-changing': line.isChanging }">
                    <!-- 伏神：只顯示地支字 -->
                    <td class="col-fushen">
                      <div v-if="line.parsed && line.parsed.fuShen">
                        <span :class="'wx-' + wuxingOfDizhi(line.parsed.fuShen.dizhi)">
                          {{ dzOnly(line.parsed.fuShen.dizhi) }}
                        </span>
                      </div>
                    </td>
                    <!-- 六親（伏神的） -->
                    <td>
                      <span v-if="line.parsed && line.parsed.fuShen" :class="'lq-' + line.parsed.fuShen.liuqin">
                        {{ fullLiuqin(line.parsed.fuShen.liuqin) }}
                      </span>
                    </td>
                    <!-- 六獸 -->
                    <td>
                      <span :class="'ls-' + line.liushen">{{ line.liushen }}</span>
                    </td>
                    <!-- 六親（本卦的） -->
                    <td>
                      <span v-if="line.parsed" :class="'lq-' + line.parsed.liuqin">
                        {{ fullLiuqin(line.parsed.liuqin) }}
                      </span>
                    </td>
                    <!-- 地支：地支字 + 日月生剋沖 / 長生訣 / 空 chip（地支字固定寬對齊） -->
                    <td class="col-dizhi">
                      <span
                        v-if="line.parsed"
                        class="dizhi-char"
                        :class="'wx-' + wuxingOfDizhi(line.parsed.dizhi)"
                      >{{ dzOnly(line.parsed.dizhi) }}</span>
                      <span
                        v-for="c in line.benChips"
                        :key="c"
                        class="ly-chip"
                        :class="{ 'ly-chip--emph': isEmphChip(c) }"
                      >{{ c }}</span>
                    </td>
                    <!-- 本卦：爻符號 + 世應（用 grid 3 欄保證 yao-symbol 永遠置中對齊） -->
                    <td class="col-bengua">
                      <div class="bengua-cell">
                        <span class="mark-left">
                          <span v-if="line.parsed && line.parsed.mark === '世'" class="mark-shi">世</span>
                          <span v-else-if="line.parsed && line.parsed.mark === '應'" class="mark-ying">應</span>
                        </span>
                        <span class="yao-symbol">
                          {{ yaoSymbolOf(line.yinyang, line.isChanging) }}
                        </span>
                        <span class="mark-right"></span>
                      </div>
                    </td>
                    <!-- 變卦：只有動爻才顯示 + 化沖/回頭剋生/化進退/化墓絕/化空 chip -->
                    <td class="col-biangua">
                      <template v-if="line.isChanging && line.bianParsed">
                        <span
                          class="dizhi-char"
                          :class="'wx-' + wuxingOfDizhi(line.bianParsed.dizhi)"
                        >{{ dzOnly(line.bianParsed.dizhi) }}</span>
                        <span
                          v-for="c in line.bianChips"
                          :key="c"
                          class="ly-chip"
                          :class="{ 'ly-chip--emph': isEmphChip(c) }"
                        >{{ c }}</span>
                      </template>
                    </td>
                    <!-- 六親（變卦的）：只有動爻才顯示 -->
                    <td>
                      <span
                        v-if="line.isChanging && line.bianParsed"
                        :class="'lq-' + line.bianParsed.liuqin"
                      >
                        {{ fullLiuqin(line.bianParsed.liuqin) }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Parse fail 時：顯示原始 pre 保底 -->
            <details v-else class="raw-fallback" open>
              <summary>⚠ 卦盤解析失敗，顯示原始 pre 格式</summary>
              <pre>{{ detail.six_lines_text || "(無)" }}</pre>
            </details>

            <!-- 五行狀態 badge 條 -->
            <div class="phase-bar" v-if="phaseArray.length > 0">
              <span
                v-for="(p, i) in phaseArray"
                :key="i"
                :class="['phase-badge', 'wx-' + p.wx]"
              >
                {{ p.wx }} {{ p.state }}
              </span>
            </div>
            <div v-else-if="detail.phase_text" class="phase-bar-raw">
              <strong>五行狀態</strong> {{ detail.phase_text }}
            </div>

            <!-- 底部宮位資訊 -->
            <div class="hex-footer">
              <div class="hex-footer-row">
                <span class="label">本卦</span>
                <span class="palace-name">{{ header.name || "(無)" }}</span>
                <span v-if="header.palace" class="palace">{{ header.palace }}</span>
                <span v-if="header.qi" class="qi">{{ header.qi }}</span>
              </div>
              <div class="hex-footer-row" v-if="hexInfo.biangua">
                <span class="label">變卦</span>
                <span class="palace-name">{{ bianguaInfo.name }}</span>
                <span v-if="bianguaInfo.palace" class="palace">{{ bianguaInfo.palace }}</span>
                <span v-if="bianguaInfo.qi" class="qi">{{ bianguaInfo.qi }}</span>
              </div>
              <div class="hex-footer-row" v-else>
                <span class="label">變卦</span>
                <span class="muted">（本卦不變）</span>
              </div>
            </div>
          </section>

          <!-- 卦身 / 用神 / 神煞（老師手動補的） -->
          <section class="shensha-display">
            <h4>▌卦身 / 用神 / 神煞</h4>
            <pre v-if="detail.shensha_notes" class="shensha-text">{{ detail.shensha_notes }}</pre>
            <p v-else class="muted">
              （尚未補。可在下方編輯區的 shensha_notes 欄位填寫）
            </p>
          </section>

          <!-- AI 解卦（摺疊） -->
          <details class="collapsible">
            <summary>▌AI 解卦（{{ (detail.ai_response || "").length }} 字）</summary>
            <pre class="ai-text">{{ detail.ai_response || "(無)" }}</pre>
          </details>

          <!-- hex_data raw JSON（摺疊） -->
          <details class="collapsible">
            <summary>▌hex_data 原始 JSON</summary>
            <pre class="json-text">{{ formatJson(detail.hex_data) }}</pre>
          </details>

          <!-- 老師編輯區 -->
          <section class="edit-section">
            <h4>═ 老師編輯區 ═</h4>

            <div class="edit-field">
              <label>
                占卦者名字
                <span class="hint-inline">（同一人多次卜卦請填一樣，之後方便 group 分析）</span>
              </label>
              <input
                v-model="editDraft.querent_name"
                type="text"
                placeholder="例：王小明"
                class="text-input"
              />
            </div>

            <div class="edit-field">
              <label class="chk-inline">
                <input v-model="editDraft.is_verified" type="checkbox" />
                <strong>已驗證</strong>
                <span class="hint-inline">（事後跟客人 follow up 過、卦準或不準都算已驗證）</span>
              </label>
            </div>

            <div class="edit-field">
              <label>
                卦身 / 用神 / 神煞
                <span class="hint-inline">
                  （自由文字。例：「卦身：丑、用神：妻財、羊刃：寅、驛馬：亥、桃花：午」）
                </span>
              </label>
              <textarea
                v-model="editDraft.shensha_notes"
                rows="4"
                class="notes-textarea"
                placeholder="卦身：（例：丑）&#10;用神：（例：妻財）&#10;神煞：羊刃/驛馬/桃花/貴人/文昌..."
              ></textarea>
            </div>

            <div class="edit-field">
              <label>
                老師心得
                <span class="hint-inline">（純文字備忘。字數 {{ editDraft.admin_notes.length }}）</span>
              </label>
              <textarea
                v-model="editDraft.admin_notes"
                rows="6"
                class="notes-textarea"
                placeholder="例：客人反饋 3 個月內果然分手，用神取子孫爻方向對"
              ></textarea>
            </div>

            <div class="edit-actions">
              <button class="btn primary" :disabled="!isDirty || saving" @click="save">
                {{ saving ? "儲存中…" : "儲存" }}
              </button>
              <button class="btn secondary" @click="closeDetail">關閉</button>
              <span v-if="saveMsg" :class="['msg', saveMsgKind]">{{ saveMsg }}</span>
              <button
                class="btn danger"
                :disabled="saving || deleting"
                @click="deleteRecord"
              >
                {{ deleting ? "刪除中…" : "刪除此筆" }}
              </button>
            </div>
          </section>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* ============================================
   常數：五行 / 六獸 / 六親
   ============================================ */
const WUXING_MAP = {
  // 天干
  甲: "木", 乙: "木", 丙: "火", 丁: "火",
  戊: "土", 己: "土", 庚: "金", 辛: "金",
  壬: "水", 癸: "水",
  // 地支
  寅: "木", 卯: "木", 巳: "火", 午: "火",
  辰: "土", 未: "土", 戌: "土", 丑: "土",
  申: "金", 酉: "金", 亥: "水", 子: "水",
};

/* ============================================
   Parse helpers（純函式，不依賴 Vue）
   ============================================ */

// 「━━━　兄己巳　　」或「官己亥　━━━　妻丙申　　」
function parseBenguaLine(s) {
  if (!s || typeof s !== "string") return null;
  const t = s.trim();

  // 有伏神：「六親+地支2字　━... 　六親+地支2字 [世/應]」
  // benguax 用全形空白（　）分隔
  const withFu =
    /^([兄父官妻孙孫子])(.{2})\s+(━━━|━\s*━)\s+([兄父官妻孙孫子])(.{2})\s*([世應应])?/;
  const m2 = t.match(withFu);
  if (m2) {
    return {
      fuShen: { liuqin: normalizeLiuqin(m2[1]), dizhi: m2[2] },
      yinyang: m2[3].includes("━ ━") || m2[3].includes("　") ? "yin" : "yang",
      liuqin: normalizeLiuqin(m2[4]),
      dizhi: m2[5],
      mark: normalizeMark(m2[6]),
    };
  }

  // 無伏神：「━... 　六親+地支2字 [世/應]」
  const plain = /^(━━━|━\s*━)\s+([兄父官妻孙孫子])(.{2})\s*([世應应])?/;
  const m1 = t.match(plain);
  if (m1) {
    return {
      fuShen: null,
      yinyang: m1[1].includes("━ ━") || m1[1].includes("　") ? "yin" : "yang",
      liuqin: normalizeLiuqin(m1[2]),
      dizhi: m1[3],
      mark: normalizeMark(m1[4]),
    };
  }

  return null;
}

function normalizeLiuqin(s) {
  if (s === "孙") return "孫";
  return s;
}

function normalizeMark(s) {
  if (!s) return null;
  if (s === "应") return "應";
  return s;
}

// 「离宫：火山旅(六合)」 → { palace, name, qi }
function parseBenguaHeader(s) {
  if (!s || typeof s !== "string") return { palace: "", name: "", qi: "" };
  const t = s.trim();
  // 「離宮：火山旅(六合)」/「離宫：火山旅（六合）」
  const m = t.match(/^(.{1,4}[宮宫])\s*[：:]?\s*([^\s(（]+)\s*[（(]?([^)）]+)?[)）]?/);
  if (!m) return { palace: "", name: t, qi: "" };
  return {
    palace: m[1] || "",
    name: m[2] || "",
    qi: m[3] || "",
  };
}

// 「木旺 火相 土休 金囚 水死」 → [{wx,state}]
function parsePhaseText(s) {
  if (!s || typeof s !== "string") return [];
  const result = [];
  const wxList = ["木", "火", "土", "金", "水"];
  const stateList = ["旺", "相", "休", "囚", "死"];
  for (const wx of wxList) {
    // 找 wx 後面接的第一個 stateList 字
    const re = new RegExp(wx + "\\s*([旺相休囚死])");
    const m = s.match(re);
    if (m && stateList.includes(m[1])) {
      result.push({ wx, state: m[1] });
    }
  }
  return result;
}

function wuxingOf(ch) {
  return WUXING_MAP[ch] || "";
}

function wuxingOfDizhi(s) {
  if (!s) return "";
  // 「己巳」→ 取「巳」；「巳」→ 取「巳」
  const c = s.length >= 2 ? s.slice(-1) : s;
  return wuxingOf(c);
}

function isDizhiKong(dizhi, xunkong) {
  // xunkong 陣列格式 = [年, 月, 日, 時]
  // 只看「日空亡」(index 2)，其他不算
  if (!dizhi || !Array.isArray(xunkong) || xunkong.length < 3) return false;
  const c = dizhi.length >= 2 ? dizhi.slice(-1) : dizhi;
  const dayKong = xunkong[2];
  return typeof dayKong === "string" && dayKong.includes(c);
}

// 全形空格 constant（避免 template text 出現全形空白觸發 ESLint）
const FW_SPACE = "　";

// 爻符號字串：陽 = ━━━ / 陰 = ━　━ / 陽動 = ━O━ / 陰動 = ━×━
function yaoSymbolOf(yinyang, isChanging) {
  if (yinyang === "yang") {
    return isChanging ? "━O━" : "━━━";
  }
  return isChanging ? "━×━" : `━${FW_SPACE}━`;
}

// 取地支的最後一字（例：「己巳」→「巳」）
function dzOnly(s) {
  if (!s) return "";
  return s.length >= 2 ? s.slice(-1) : s;
}

// 從 hex_data.gongli（「2026年1月11日13时0分」）抽出「2026年1月11日」
function formatGongli(s) {
  if (!s || typeof s !== "string") return "";
  const m = s.match(/^(\d+年\d+月\d+日)/);
  return m ? m[1] : "";
}

/**
 * 從 six_lines_text 一行文字判斷陰陽 + 動化
 * 規則（以 describeSixLines 輸出為準）：
 *   含「陽爻」（靜）→ yang, static
 *   含「陰爻」（靜）→ yin, static
 *   含「老陽」+「動化」→ yang, changing
 *   含「老陰」+「動化」→ yin, changing
 * @returns {{yinyang: "yang"|"yin", isChanging: boolean} | null}
 */
function parseYaoFromLine(line) {
  if (!line || typeof line !== "string") return null;
  const t = line;
  if (t.includes("老陽") && t.includes("動化")) return { yinyang: "yang", isChanging: true };
  if (t.includes("老陰") && t.includes("動化")) return { yinyang: "yin", isChanging: true };
  if (t.includes("陽爻")) return { yinyang: "yang", isChanging: false };
  if (t.includes("陰爻")) return { yinyang: "yin", isChanging: false };
  return null;
}

/* ============================================
   六親全名
   ============================================ */
const LIUQIN_FULL = {
  妻: "妻財",
  父: "父母",
  官: "官鬼",
  兄: "兄弟",
  孫: "子孫",
  孙: "子孫",
  子: "子孫",
};
function fullLiuqin(s) {
  return LIUQIN_FULL[s] || s || "";
}

/* ============================================
   Chip 分類：這些 chip 用強調樣式（有底色）
   ============================================ */
const EMPH_CHIP_SET = new Set(["空", "化空", "化進", "化退"]);

/* ============================================
   日月生剋沖 / 化進退 / 寄生十二宮 常數表
   ============================================ */
const BRANCH_CLASH = {
  子: "午", 午: "子", 丑: "未", 未: "丑",
  寅: "申", 申: "寅", 卯: "酉", 酉: "卯",
  辰: "戌", 戌: "辰", 巳: "亥", 亥: "巳",
};
const GENERATE = { 木: "火", 火: "土", 土: "金", 金: "水", 水: "木" };
const CONTROL = { 木: "土", 土: "水", 水: "火", 火: "金", 金: "木" };

// 化進 / 化退：木火金水各 1 對，土為四庫循環（辰→未→戌→丑→辰）
const ADVANCE_PAIR = {
  寅: "卯", 巳: "午", 申: "酉", 亥: "子",
  辰: "未", 未: "戌", 戌: "丑", 丑: "辰",
};
const RETREAT_PAIR = {
  卯: "寅", 午: "巳", 酉: "申", 子: "亥",
  未: "辰", 戌: "未", 丑: "戌", 辰: "丑",
};

// 五行寄生十二宮：本爻地支 → { 長生, 沐浴, 帝旺, 墓, 絕 }
// 水土同宮（辰戌丑未 + 亥子 共 6 支用同一組），符合傳統六爻
const LIFE_CYCLE = {
  寅: { chang: "亥", mu: "子", di: "卯", ku: "未", jue: "申" },
  卯: { chang: "亥", mu: "子", di: "卯", ku: "未", jue: "申" },
  巳: { chang: "寅", mu: "卯", di: "午", ku: "戌", jue: "亥" },
  午: { chang: "寅", mu: "卯", di: "午", ku: "戌", jue: "亥" },
  申: { chang: "巳", mu: "午", di: "酉", ku: "丑", jue: "寅" },
  酉: { chang: "巳", mu: "午", di: "酉", ku: "丑", jue: "寅" },
  辰: { chang: "申", mu: "酉", di: "子", ku: "辰", jue: "巳" },
  戌: { chang: "申", mu: "酉", di: "子", ku: "辰", jue: "巳" },
  丑: { chang: "申", mu: "酉", di: "子", ku: "辰", jue: "巳" },
  未: { chang: "申", mu: "酉", di: "子", ku: "辰", jue: "巳" },
  亥: { chang: "申", mu: "酉", di: "子", ku: "辰", jue: "巳" },
  子: { chang: "申", mu: "酉", di: "子", ku: "辰", jue: "巳" },
};

function computeBenguaChips({ benBranch, benWx, dayBranch, dayWx, monBranch, monWx, isKong }) {
  const chips = [];
  const cycle = LIFE_CYCLE[benBranch];
  // 日建
  if (dayBranch && BRANCH_CLASH[dayBranch] === benBranch) chips.push("日沖");
  if (dayWx && CONTROL[dayWx] === benWx) chips.push("日剋爻");
  if (dayWx && GENERATE[dayWx] === benWx) chips.push("日生爻");
  if (cycle && dayBranch === cycle.chang) chips.push("長生");
  if (cycle && dayBranch === cycle.mu) chips.push("沐浴");
  if (cycle && dayBranch === cycle.di) chips.push("帝旺");
  if (cycle && dayBranch === cycle.ku) chips.push("入日墓");
  // 月建：同月五行 → 「在月旺」；月生爻 → 「在月相」；月剋爻 → 「在月死」
  if (monBranch && BRANCH_CLASH[monBranch] === benBranch) chips.push("月沖");
  if (monWx && monWx === benWx) chips.push("在月旺");
  if (monWx && GENERATE[monWx] === benWx) chips.push("在月相");
  if (monWx && CONTROL[monWx] === benWx) chips.push("在月死");
  // 空亡
  if (isKong) chips.push("空");
  return chips;
}

function computeBianguaChips({ benBranch, benWx, bianBranch, bianWx, bianKong }) {
  const chips = [];
  const cycle = LIFE_CYCLE[benBranch];
  if (BRANCH_CLASH[benBranch] === bianBranch) chips.push("化沖");
  if (CONTROL[bianWx] === benWx) chips.push("回頭剋");
  if (GENERATE[bianWx] === benWx) chips.push("回頭生");
  if (ADVANCE_PAIR[benBranch] === bianBranch) chips.push("化進");
  if (RETREAT_PAIR[benBranch] === bianBranch) chips.push("化退");
  if (cycle && bianBranch === cycle.ku) chips.push("化墓");
  if (cycle && bianBranch === cycle.jue) chips.push("化絕");
  if (bianKong) chips.push("化空");
  return chips;
}

export default {
  name: "LiuYaoRecordsPanel",
  props: {
    apiBaseUrl: { type: String, required: true },
    adminToken: { type: String, required: true },
  },
  data() {
    return {
      items: [],
      total: 0,
      page: 1,
      pageSize: 20,

      searchQ: "",
      currentQ: "",
      verifiedOnly: false,

      loading: false,
      error: "",

      detail: null,
      editDraft: {
        querent_name: "",
        is_verified: false,
        admin_notes: "",
        shensha_notes: "",
      },
      saving: false,
      saveMsg: "",
      saveMsgKind: "ok",
      deleting: false,
    };
  },

  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.total / this.pageSize));
    },

    isDirty() {
      if (!this.detail) return false;
      return (
        this.editDraft.querent_name !== (this.detail.querent_name || "") ||
        this.editDraft.is_verified !== !!this.detail.is_verified ||
        this.editDraft.admin_notes !== (this.detail.admin_notes || "") ||
        this.editDraft.shensha_notes !== (this.detail.shensha_notes || "")
      );
    },

    // hex_data 常用資訊萃取
    hexInfo() {
      const h = this.detail?.hex_data || {};
      return {
        gongli: h.gongli || "",
        nongli: h.nongli || "",
        ganzhi: Array.isArray(h.ganzhi) ? h.ganzhi : [],
        xunkong: Array.isArray(h.xunkong) ? h.xunkong : [],
        benguax: Array.isArray(h.benguax) ? h.benguax : [],
        bianguax: Array.isArray(h.bianguax) ? h.bianguax : [],
        liushen: Array.isArray(h.liushen) ? h.liushen : [],
        bengua: h.bengua || "",
        biangua: h.biangua || "",
      };
    },

    // 四柱顯示順序：時、日、月、年（右到左，模仿傳統排盤）
    ganzhiRev() {
      const gz = this.hexInfo.ganzhi;
      if (gz.length !== 4) return [];
      // ganzhi 陣列是 [年, 月, 日, 時]，顯示要 [時, 日, 月, 年]
      return [gz[3], gz[2], gz[1], gz[0]];
    },

    // 主卦盤：從上爻到初爻的 6 條
    hexLines() {
      const bg = this.hexInfo.benguax;
      const bian = this.hexInfo.bianguax;
      const shen = this.hexInfo.liushen;
      const xunkong = this.hexInfo.xunkong;
      const ganzhi = this.hexInfo.ganzhi;
      if (bg.length !== 6) return [];

      // 日建 / 月建（ganzhi 格式 [年, 月, 日, 時]）
      const dayGz = ganzhi.length === 4 ? ganzhi[2] : "";
      const monGz = ganzhi.length === 4 ? ganzhi[1] : "";
      const dayBranch = dzOnly(dayGz);
      const monBranch = dzOnly(monGz);
      const dayWx = wuxingOf(dayBranch);
      const monWx = wuxingOf(monBranch);

      // 從 six_lines_text 拆出每爻的文字描述，用於判斷陰陽 / 動化
      // （用「陽爻/陰爻/老陽動化/老陰動化」關鍵字，比看 benguax 符號更穩）
      const textLines = String(this.detail?.six_lines_text || "")
        .split("\n")
        .filter((s) => s.trim());

      // 實測 benguax 陣列 index 0 = 上爻（跟 liushen 一致，玄武對應上爻）
      // 表格從上到下渲染 → 直接依 index 對應，不需要 reverse
      return bg.map((line, i) => {
        const parsed = parseBenguaLine(line);
        const bianRaw = bian[i] || null;
        const bianParsed = bianRaw ? parseBenguaLine(bianRaw) : null;

        // 以 six_lines_text 該行判斷陰陽 / 動化（優先）
        // 如果 six_lines_text 沒有或格式對不上，fallback 用 parseBenguaLine 的結果 + bianguax 是否 null
        const textYao = parseYaoFromLine(textLines[i] || "");
        const yinyang = textYao?.yinyang ?? parsed?.yinyang ?? "yang";
        const isChanging =
          textYao?.isChanging !== undefined ? textYao.isChanging : bianRaw !== null;

        const isKong = parsed ? isDizhiKong(parsed.dizhi, xunkong) : false;

        // 本卦 chip：日月生剋沖 / 長生訣 / 空
        const benBranch = parsed ? dzOnly(parsed.dizhi) : "";
        const benWx = parsed ? wuxingOfDizhi(parsed.dizhi) : "";
        const benChips = parsed
          ? computeBenguaChips({ benBranch, benWx, dayBranch, dayWx, monBranch, monWx, isKong })
          : [];

        // 變卦 chip：僅動爻才計算
        let bianChips = [];
        if (isChanging && bianParsed) {
          const bianBranch = dzOnly(bianParsed.dizhi);
          const bianWx = wuxingOfDizhi(bianParsed.dizhi);
          const bianKong = isDizhiKong(bianParsed.dizhi, xunkong);
          bianChips = computeBianguaChips({ benBranch, benWx, bianBranch, bianWx, bianKong });
        }

        return {
          idx: i, // 0 = 上爻
          parsed,
          raw: line,
          bianRaw,
          bianParsed,
          yinyang, // ← 以 six_lines_text 為主
          isChanging, // ← 以 six_lines_text 為主
          isKong,
          benChips,
          bianChips,
          liushen: normalizeLiushen(shen[i] || ""),
        };
      });
    },

    header() {
      return parseBenguaHeader(this.hexInfo.bengua);
    },
    bianguaInfo() {
      return parseBenguaHeader(this.hexInfo.biangua);
    },
    phaseArray() {
      return parsePhaseText(this.detail?.phase_text || "");
    },

    showRawFallback() {
      const lines = this.hexLines;
      if (lines.length !== 6) return true;
      // 6 條裡若有超過 1 條 parse 失敗 → fallback
      return lines.filter((l) => !l.parsed).length > 1;
    },
  },

  mounted() {
    this.fetchList();
  },

  methods: {
    async fetchList() {
      this.loading = true;
      this.error = "";
      try {
        const url = new URL(
          `${this.apiBaseUrl}/api/admin/liuyao-records`,
          window.location.origin,
        );
        url.searchParams.set("page", this.page);
        url.searchParams.set("pageSize", this.pageSize);
        if (this.currentQ) url.searchParams.set("q", this.currentQ);
        if (this.verifiedOnly) url.searchParams.set("verified", "1");

        const res = await fetch(url.toString(), {
          headers: { "x-admin-token": this.adminToken },
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
        this.items = Array.isArray(data.items) ? data.items : [];
        this.total = Number(data.total) || 0;
      } catch (err) {
        this.error = String(err?.message || err);
      } finally {
        this.loading = false;
      }
    },

    onSearch() {
      this.currentQ = this.searchQ.trim();
      this.page = 1;
      this.fetchList();
    },

    goto(p) {
      const target = Math.max(1, Math.min(this.totalPages, p));
      if (target === this.page) return;
      this.page = target;
      this.fetchList();
    },

    async openDetail(id) {
      this.saveMsg = "";
      try {
        const res = await fetch(
          `${this.apiBaseUrl}/api/admin/liuyao-records/${id}`,
          { headers: { "x-admin-token": this.adminToken } },
        );
        const data = await res.json();
        if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
        this.detail = data;
        this.editDraft = {
          querent_name: data.querent_name || "",
          is_verified: !!data.is_verified,
          admin_notes: data.admin_notes || "",
          shensha_notes: data.shensha_notes || "",
        };
      } catch (err) {
        alert("開啟失敗：" + (err?.message || err));
      }
    },

    closeDetail() {
      if (this.isDirty) {
        if (!confirm("有未儲存的變更，確定關閉？")) return;
      }
      this.detail = null;
      this.editDraft = {
        querent_name: "",
        is_verified: false,
        admin_notes: "",
        shensha_notes: "",
      };
      this.saveMsg = "";
    },

    async save() {
      if (!this.detail || !this.isDirty) return;
      this.saving = true;
      this.saveMsg = "";
      try {
        const res = await fetch(
          `${this.apiBaseUrl}/api/admin/liuyao-records/${this.detail.id}`,
          {
            method: "PATCH",
            headers: {
              "x-admin-token": this.adminToken,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              querent_name: this.editDraft.querent_name,
              is_verified: this.editDraft.is_verified,
              admin_notes: this.editDraft.admin_notes,
              shensha_notes: this.editDraft.shensha_notes,
            }),
          },
        );
        const data = await res.json();
        if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);

        this.detail.querent_name = this.editDraft.querent_name;
        this.detail.is_verified = this.editDraft.is_verified;
        this.detail.admin_notes = this.editDraft.admin_notes;
        this.detail.shensha_notes = this.editDraft.shensha_notes;

        // 同步刷新列表
        const row = this.items.find((r) => r.id === this.detail.id);
        if (row) {
          row.querent_name = this.editDraft.querent_name;
          row.is_verified = this.editDraft.is_verified;
          row.has_notes = !!this.editDraft.admin_notes.trim();
          row.has_shensha = !!this.editDraft.shensha_notes.trim();
        }

        this.saveMsg = "已儲存 ✓";
        this.saveMsgKind = "ok";
      } catch (err) {
        this.saveMsg = "儲存失敗：" + (err?.message || err);
        this.saveMsgKind = "err";
      } finally {
        this.saving = false;
      }
    },

    async deleteRecord() {
      if (!this.detail || this.deleting || this.saving) return;
      const id = this.detail.id;
      const label = this.detail.querent_name || this.detail.topic_text || `#${id}`;
      if (!confirm(`確定要刪除這筆六爻紀錄嗎？\nID: ${id}（${label}）\n刪掉就回不來。`)) return;

      this.deleting = true;
      this.saveMsg = "";
      try {
        const res = await fetch(
          `${this.apiBaseUrl}/api/admin/liuyao-records/${id}`,
          {
            method: "DELETE",
            headers: { "x-admin-token": this.adminToken },
          },
        );
        if (!res.ok) {
          const data = await res.json().catch(() => null);
          throw new Error(data?.error || `HTTP ${res.status}`);
        }
        this.items = this.items.filter((r) => r.id !== id);
        this.total = Math.max(0, this.total - 1);
        this.detail = null;
        this.editDraft = {
          querent_name: "",
          is_verified: false,
          admin_notes: "",
          shensha_notes: "",
        };
        alert("已刪除 ✅");
        if (this.items.length === 0 && this.page > 1) {
          this.page -= 1;
        }
        this.fetchList();
      } catch (err) {
        this.saveMsg = "刪除失敗：" + (err?.message || err);
        this.saveMsgKind = "err";
      } finally {
        this.deleting = false;
      }
    },

    /* ============================================
       格式化 helpers（供 template 用）
       ============================================ */
    formatDt(iso) {
      if (!iso) return "";
      const d = new Date(iso);
      const pad = (n) => String(n).padStart(2, "0");
      return `${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },
    formatFullDt(iso) {
      if (!iso) return "";
      const d = new Date(iso);
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())} (台北)`;
    },
    formatJson(obj) {
      if (!obj) return "(無)";
      try {
        return JSON.stringify(obj, null, 2);
      } catch {
        return String(obj);
      }
    },
    // 提供給 template 用（因為 template 內不能直接呼叫 module-level function）
    wuxingOf,
    wuxingOfDizhi,
    yaoSymbolOf,
    dzOnly,
    fullLiuqin,
    formatGongli,
    isEmphChip(c) {
      return EMPH_CHIP_SET.has(c);
    },
  },
};

// 六獸名稱正規化（簡繁 / 空白）
function normalizeLiushen(s) {
  if (!s) return "";
  const t = String(s).trim();
  const map = { 螣蛇: "螣蛇", 腾蛇: "螣蛇", 勾陈: "勾陳", 青龙: "青龍" };
  return map[t] || t;
}
</script>

<style scoped>
/* ============================================
   Panel 基本
   ============================================ */
.lyr-panel {
  padding: 16px;
  max-width: 1100px;
  margin: 0 auto;
}
.lyr-header h2 {
  margin: 0 0 4px;
  font-size: 20px;
}
.lyr-header .hint {
  color: #666;
  font-size: 13px;
  margin: 4px 0 12px;
}

.error-banner {
  background: #fff2f0;
  border: 1px solid #ffb8b0;
  color: #c33;
  padding: 8px 12px;
  border-radius: 4px;
  margin-bottom: 12px;
  font-size: 13px;
}

.toolbar {
  display: flex;
  gap: 8px;
  align-items: center;
  margin: 12px 0;
  flex-wrap: wrap;
}
.search-input {
  flex: 1;
  min-width: 240px;
  padding: 6px 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
}
.chk-inline {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: #555;
  cursor: pointer;
  user-select: none;
}
.chk-inline input {
  cursor: pointer;
}

.btn {
  padding: 6px 14px;
  border: 1px solid #3a7;
  background: #3a7;
  color: #fff;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}
.btn:disabled {
  background: #aaa;
  border-color: #aaa;
  cursor: not-allowed;
}
.btn.secondary {
  background: #fff;
  color: #555;
  border-color: #ccc;
}
.btn.primary {
  background: #3a7;
  border-color: #2c8a2c;
}
.btn.small {
  padding: 4px 10px;
  font-size: 12px;
}

.loading,
.empty {
  padding: 24px;
  text-align: center;
  color: #999;
}

/* ============================================
   列表
   ============================================ */
.lyr-table {
  width: 100%;
  border-collapse: collapse;
  margin: 12px 0;
  font-size: 13px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
}
.lyr-table th,
.lyr-table td {
  padding: 8px 10px;
  border-bottom: 1px solid #eee;
  text-align: left;
  vertical-align: middle;
}
.lyr-table thead th {
  background: #f5f1e8;
  color: #6b4f2a;
  font-weight: 600;
  border-bottom: 2px solid #d4c8a8;
  white-space: nowrap;
}
.lyr-table tbody tr:hover {
  background: #fafaf5;
}
.lyr-table tbody tr.verified {
  background: #f7fef7;
}
.lyr-table tbody tr.verified:hover {
  background: #eef9ee;
}

.col-id { width: 60px; }
.col-time { width: 130px; white-space: nowrap; }
.col-querent { width: 100px; }
.col-gender { width: 60px; }
.col-hex { width: 100px; }
.col-action { width: 100px; text-align: right; }
.topic-cell {
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.action-cell {
  text-align: right;
  white-space: nowrap;
}
.action-cell .badge {
  margin-left: 6px;
  font-size: 14px;
  vertical-align: middle;
}
.badge.verified { color: #2c8a2c; }

.mono {
  font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
}
.small { font-size: 12px; }
.muted {
  color: #bbb;
  font-style: italic;
}

.pager {
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
  margin: 16px 0;
  font-size: 13px;
  color: #666;
}

/* ============================================
   Modal
   ============================================ */
.modal-mask {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 40px 20px;
  z-index: 100;
  overflow-y: auto;
}
.modal {
  background: #fff;
  border-radius: 8px;
  width: 100%;
  max-width: 900px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 80px);
}
.modal-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
}
.modal-head h3 { margin: 0; font-size: 16px; }
.close-btn {
  background: transparent;
  border: none;
  font-size: 22px;
  cursor: pointer;
  color: #999;
  line-height: 1;
}
.close-btn:hover { color: #333; }
.modal-body {
  padding: 16px 20px;
  overflow-y: auto;
}

.meta {
  background: #fafafa;
  border: 1px solid #eee;
  border-radius: 4px;
  padding: 10px 14px;
  font-size: 13px;
}
.meta-row {
  display: flex;
  gap: 12px;
  padding: 3px 0;
}
.meta-row label {
  color: #888;
  width: 60px;
  flex-shrink: 0;
}

/* ============================================
   六爻卦盤（hex-section）
   ============================================ */
.hex-section {
  margin-top: 16px;
  border: 1px solid #d4c8a8;
  border-radius: 6px;
  background: #fdfbf5;
  padding: 12px;
}

/* 頂部：日期 + 四柱 */
.hex-topbar {
  display: flex;
  gap: 12px;
  align-items: stretch;
  background: #eef0f2;
  padding: 8px 10px;
  border-radius: 4px;
  margin-bottom: 8px;
}
.hex-datebox {
  flex: 1;
  font-size: 12px;
  color: #333;
}
.date-gongli {
  font-size: 14px;
  font-weight: 600;
  color: #333;
}
.date-nongli {
  margin-top: 3px;
  color: #666;
}

.ganzhi-table {
  border-collapse: collapse;
  font-size: 15px;
  text-align: center;
  min-width: 200px;
}
.ganzhi-table th {
  background: #b8a888;
  color: #fff;
  padding: 3px 12px;
  font-weight: 500;
  border: 1px solid #a89878;
  font-size: 12px;
}
.ganzhi-table td {
  padding: 3px 12px;
  background: #fff;
  border: 1px solid #d4c8a8;
  vertical-align: middle;
  line-height: 1.4;
  font-weight: 600;
}

/* 空亡 bar */
.xunkong-bar {
  background: #f5f1e8;
  padding: 6px 10px;
  border-radius: 4px;
  font-size: 12px;
  color: #6b4f2a;
  margin-bottom: 8px;
}
.xunkong-bar strong {
  margin-right: 12px;
  color: #8b6f47;
}

/* 主卦盤 8 欄 */
.hex-chart-wrap {
  overflow-x: auto;
}
.hex-chart {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  background: #fff;
  border: 1px solid #d4c8a8;
}
.hex-chart th {
  background: #f5f1e8;
  color: #6b4f2a;
  padding: 6px 4px;
  font-weight: 600;
  border: 1px solid #d4c8a8;
  font-size: 12px;
  text-align: center;
  white-space: nowrap;
}
.hex-chart td {
  border: 1px solid #e5e0d0;
  padding: 8px 4px;
  text-align: center;
  vertical-align: middle;
  min-width: 44px;
  font-weight: 500;
  line-height: 1.3;
}
.hex-chart tbody tr.is-changing {
  background: #fffbe6;
}

.col-fushen,
.col-bengua,
.col-dizhi,
.col-biangua {
  font-size: 14px;
}
/* 地支/變卦欄：靠左，chip 排在地支字後方；地支字固定寬讓行間對齊
   selector 提高 specificity（`.hex-chart td` 平手時後定義 wins，這裡加 td 保證覆蓋） */
.hex-chart td.col-dizhi,
.hex-chart td.col-biangua {
  text-align: left;
  padding-left: 10px;
  padding-right: 6px;
}
.dizhi-char {
  display: inline-block;
  width: 1.6em;              /* 固定寬（不用 min-width），保證每行地支字 x 一致 */
  text-align: center;
  vertical-align: middle;
  font-weight: 500;
}

/* 本卦欄用 3 欄 grid：左 mark / yao / 右 mark，保證 yao 永遠置中 */
.bengua-cell {
  display: grid;
  grid-template-columns: 28px auto 12px;
  align-items: center;
  justify-items: center;
  gap: 4px;
}
.mark-left,
.mark-right {
  display: inline-block;
  min-width: 24px;
}

/* 爻符號：字元寬度用等寬字保證陰/陽/動爻對齊 */
.yao-symbol {
  font-family: "PingFang TC", "Noto Sans Mono CJK TC", ui-monospace, "SF Mono",
    Menlo, Consolas, monospace;
  letter-spacing: 0;
  font-weight: bold;
  font-size: 16px;
  color: #333;
  display: inline-block;
  min-width: 3.2em;
  text-align: center;
  white-space: pre;
}
/* 動爻整條爻符號變紅（含中間的 O/X） */
.is-changing .yao-symbol {
  color: #c33a3a;
}
.mark-shi {
  display: inline-block;
  background: #ffe082;
  color: #6b4f2a;
  padding: 1px 4px;
  border-radius: 3px;
  font-size: 11px;
  font-weight: bold;
  margin-left: 4px;
}
.mark-ying {
  display: inline-block;
  background: #b3e5fc;
  color: #1e5aa8;
  padding: 1px 4px;
  border-radius: 3px;
  font-size: 11px;
  font-weight: bold;
  margin-left: 4px;
}
.mark-kong {
  display: inline-block;
  background: #fff59d;
  color: #8b6f47;
  padding: 0 3px;
  border-radius: 2px;
  font-size: 10px;
  margin-left: 3px;
  font-weight: 600;
}
/* 日月生剋沖 / 長生訣 / 化進退 chip：對齊 admin 暖色 palette */
.ly-chip {
  display: inline-block;
  margin: 0 0 0 4px;
  padding: 1px 5px;
  border: 0.5px solid #b8935a;   /* camel */
  color: #8b6f47;                /* umber */
  background: transparent;
  font: 9.5px "SF Mono", "JetBrains Mono", "Roboto Mono", ui-monospace, monospace;
  letter-spacing: 0.08em;
  vertical-align: middle;
  white-space: nowrap;
  border-radius: 2px;
}
/* 強調 chip（空 / 化空 / 化進 / 化退）：加暖色底 */
.ly-chip--emph {
  border-color: #c9a961;   /* amber */
  background: #e8d9b8;     /* oracle-bg-3 */
}

/* ============================================
   五行配色（gan/zhi/wx-* / lq-* / ls-*）
   ============================================ */
.wx-木 { color: #2f7a2f; }
.wx-火 { color: #c33a3a; }
.wx-土 { color: #8b6f47; }
.wx-金 { color: #d4802a; }
.wx-水 { color: #1e5aa8; }

/* 六獸配色（螣蛇 = 勾陳 同棕） */
.ls-玄武 { color: #1e5aa8; }
.ls-白虎 { color: #d4802a; }
.ls-螣蛇 { color: #8b6f47; }
.ls-勾陳 { color: #8b6f47; }
.ls-朱雀 { color: #c33a3a; }
.ls-青龍 { color: #2f7a2f; }

/* 六親配色 */
.lq-兄 { color: #555; }
.lq-父 { color: #8b4513; }
.lq-官 { color: #6a1b9a; }
.lq-妻 { color: #8b6f47; }
.lq-孫,
.lq-孙 { color: #2f7a2f; }
.lq-子 { color: #2f7a2f; }

/* ============================================
   五行狀態 badge 條 + 底部宮位資訊
   ============================================ */
.phase-bar {
  display: flex;
  gap: 8px;
  padding: 10px;
  background: #f5f1e8;
  border-radius: 4px;
  margin-top: 10px;
  justify-content: center;
  flex-wrap: wrap;
}
.phase-badge {
  display: inline-block;
  padding: 4px 12px;
  background: #fff;
  border: 1px solid #d4c8a8;
  border-radius: 3px;
  font-size: 13px;
  font-weight: 600;
}
.phase-bar-raw {
  background: #f5f1e8;
  padding: 8px 10px;
  border-radius: 4px;
  margin-top: 10px;
  font-size: 12px;
  color: #6b4f2a;
}
.phase-bar-raw strong {
  margin-right: 8px;
}

.hex-footer {
  margin-top: 10px;
  padding: 10px;
  background: #fafafa;
  border: 1px solid #eee;
  border-radius: 4px;
  font-size: 13px;
}
.hex-footer-row {
  display: flex;
  gap: 10px;
  align-items: center;
  padding: 3px 0;
}
.hex-footer-row .label {
  color: #888;
  width: 40px;
  flex-shrink: 0;
}
.hex-footer-row .palace-name {
  font-weight: 600;
  color: #333;
}
.hex-footer-row .palace {
  color: #6b4f2a;
  font-size: 12px;
  background: #f5f1e8;
  padding: 1px 6px;
  border-radius: 3px;
}
.hex-footer-row .qi {
  color: #c33a3a;
  font-size: 12px;
  background: #ffe0e0;
  padding: 1px 6px;
  border-radius: 3px;
}

.raw-fallback {
  border: 1px dashed #f0c090;
  padding: 8px;
  border-radius: 4px;
  margin: 10px 0;
  background: #fffaf0;
}
.raw-fallback summary {
  cursor: pointer;
  color: #c33a3a;
  font-weight: 500;
  font-size: 13px;
}
.raw-fallback pre {
  margin: 8px 0 0;
  font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  font-size: 12px;
  white-space: pre-wrap;
}

/* ============================================
   卦身/用神/神煞 顯示區
   ============================================ */
.shensha-display {
  margin-top: 14px;
  padding: 10px 14px;
  background: #f3f0ff;
  border: 1px solid #d0c8f0;
  border-radius: 4px;
}
.shensha-display h4 {
  margin: 0 0 6px;
  font-size: 13px;
  color: #6a1b9a;
  font-weight: 600;
}
.shensha-text {
  margin: 0;
  font-family: inherit;
  font-size: 13px;
  line-height: 1.7;
  white-space: pre-wrap;
  word-break: break-word;
  color: #333;
}

/* ============================================
   collapsibles (AI / hex_data raw)
   ============================================ */
.collapsible {
  margin-top: 14px;
  border: 1px solid #eee;
  border-radius: 4px;
}
.collapsible summary {
  padding: 8px 12px;
  cursor: pointer;
  user-select: none;
  font-size: 13px;
  color: #555;
  font-weight: 500;
  background: #fafafa;
}
.collapsible summary:hover {
  background: #f0f0f0;
}
.collapsible[open] summary {
  border-bottom: 1px solid #eee;
}
.ai-text,
.json-text {
  margin: 0;
  padding: 10px 14px;
  font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  font-size: 12px;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 400px;
  overflow-y: auto;
  background: #fff;
}
.ai-text { color: #333; }
.json-text { color: #555; }

/* ============================================
   編輯區
   ============================================ */
.edit-section {
  margin-top: 20px;
  padding: 16px;
  background: #fffef5;
  border: 1px solid #f0e5b8;
  border-radius: 4px;
}
.edit-section h4 {
  margin: 0 0 12px;
  font-size: 14px;
  color: #6b4f2a;
  text-align: center;
  letter-spacing: 2px;
}
.edit-field {
  margin-bottom: 12px;
}
.edit-field label {
  display: block;
  margin-bottom: 4px;
  font-size: 13px;
  font-weight: 500;
  color: #555;
}
.hint-inline {
  font-weight: normal;
  color: #999;
  font-size: 12px;
  margin-left: 6px;
}
.text-input {
  width: 100%;
  padding: 6px 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
  box-sizing: border-box;
}
.notes-textarea {
  width: 100%;
  padding: 8px;
  font-family: inherit;
  font-size: 13px;
  line-height: 1.6;
  border: 1px solid #ccc;
  border-radius: 4px;
  resize: vertical;
  box-sizing: border-box;
}
.edit-actions {
  margin-top: 12px;
  display: flex;
  gap: 8px;
  align-items: center;
}
.msg {
  font-size: 13px;
  margin-left: 8px;
}
.msg.ok { color: #2c8a2c; }
.msg.err { color: #c33; }

.btn.danger {
  background: #c33;
  border-color: #a22;
  color: #fff;
  margin-left: auto;
}
.btn.danger:hover:not(:disabled) {
  background: #a22;
}
</style>
